import { useRollbar } from '@rollbar/react'
import { ButtonActionTypeEnum } from 'common/enums/ButtonActionTypeEnum'
import { BadRequest, NetworkError } from 'common/errors'
import { optInFail } from 'publisher/actions/optInActions'
import { MemberSubmitData, sendForm } from 'publisher/api/member-access'
import useManagement, {
  selectors as managementSel,
} from 'publisher/hooks/useManagement'
import useOptIn, { selectors as optInSelectors } from 'publisher/hooks/useOptIn'
import { validateEmptyOptInFields } from 'publisher/utils/fieldValidation'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { WebsiteLoginButtonEntityInterface } from 'src/shared/entities/button/website-login-button.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { extractResetToken } from '../utils/extract-reset-token'

const useWebsiteLoginSubmit = (button: WebsiteLoginButtonEntityInterface) => {
  const rollbar = useRollbar()
  const { t } = useTypedTranslation()
  const dispatch = useDispatch()
  const [isLoading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const fields = useOptIn(optInSelectors.getCustomerOptInFields)
  const confirmedExplicitConsentsIds = useManagement(
    managementSel.getConfirmedExplicitConsentsIds,
  ).length
  const rememberMe = confirmedExplicitConsentsIds > 0
  const calculatedEmail = fields.email ? fields.email : ''
  const calculatedPassword = fields.password ? fields.password : ''
  const calculatedCode = fields.code ? fields.code : ''
  let formData: MemberSubmitData
  let apiUrl = location.href
  let redirectUrl = ''
  const confirmRegistrationToken =
    typeof window !== 'undefined'
      ? window.location.pathname.split('/').pop()
      : ''

  switch (button.action) {
    case ButtonActionTypeEnum.WebsiteLogin:
      redirectUrl = '/school'
      apiUrl = '/api/security/login'
      formData = {
        email: calculatedEmail,
        password: calculatedPassword,
        rememberMe: rememberMe,
      }
      break
    case ButtonActionTypeEnum.WebsiteTwoFactorAuthLink:
      redirectUrl = '/school'
      apiUrl = '/2fa_check'
      formData = {
        _auth_code: calculatedCode,
        _trusted: rememberMe,
      }
      break
    case ButtonActionTypeEnum.WebsiteRegistration:
      formData = {
        email: calculatedEmail,
      }
      break
    case ButtonActionTypeEnum.WebsiteConfirmRegistration:
      apiUrl = '/api/security/register/confirm'
      formData = {
        plainPassword: calculatedPassword,
        firstName: fields.first_name,
        lastName: fields.surname,
        token: confirmRegistrationToken as string,
      }
      break
    case ButtonActionTypeEnum.WebsiteResetPassword:
      apiUrl = '/api/security/reset'
      formData = {
        token: extractResetToken(),
        password: calculatedPassword,
      }
      break
    case ButtonActionTypeEnum.WebsiteResetRequest:
      redirectUrl = '/dashboard/check-email'
      apiUrl = '/api/security/send-reset'
      formData = {
        email: calculatedEmail,
      }
      break
    default:
      rollbar.error(
        `Unknown button action type in custom login form ${button.action}`,
      )
  }

  const submit = async () => {
    const slugs = Object.keys(fields)
    const fieldsForValidationFormat = slugs.reduce((acc, slug) => {
      // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
      return { ...acc, [slug]: { value: fields[slug] } }
    }, {})
    const emptyFieldErrors = validateEmptyOptInFields(
      fieldsForValidationFormat,
      t,
    )
    if (Object.keys(emptyFieldErrors).length) {
      const common: string[] = []
      dispatch(optInFail({ common, fields: emptyFieldErrors }))
      return
    }
    if (
      button.action === ButtonActionTypeEnum.WebsiteConfirmRegistration &&
      calculatedPassword !== fields.confirm_password
    ) {
      const common: string[] = []
      dispatch(
        optInFail({
          common,
          fields: { confirm_password: [t('validation.password.mismatch')] },
        }),
      )
      return
    }

    setLoading(true)
    try {
      const response = await sendForm(apiUrl, formData)
      window.location.replace(response.data.redirect || redirectUrl)
    } catch (error) {
      if (error instanceof BadRequest) {
        setErrors(error.response.data.errors.common)
        const fieldsErrors = error.response.data.errors.fields || {}
        dispatch(
          optInFail({
            fields: {
              ...fieldsErrors,
              password: fieldsErrors.plainPassword || [],
              surname: fieldsErrors.lastName || [],
              first_name: fieldsErrors.firstName || [],
            },
          }),
        )
        window.dispatchEvent(new Event('form_updated'))
      } else if (error instanceof NetworkError) {
        setErrors([t('core.errors.no_connection')])
      } else {
        rollbar.error(`Custom login error`, error as Error)
      }
    }
    setLoading(false)
  }

  return [isLoading, errors, submit] as const
}

export default useWebsiteLoginSubmit
